/**
 * How a team member looks: one of the workbench's pixel characters by index (`avatar`, 0 to 40), an
 * https picture, or a person's picture from the organisation (`GET /people/v1/:id/picture`). The
 * character sheets are mirrored in `characters/` (MIRROR.md); the idle frame is a CSS background
 * on a 16 x 32 box, so nothing is drawn on a canvas.
 */

import { MEMBER_CHARACTER_COUNT, type OrgTeamMemberV2 } from '@aioe/shared';
import { ChevronDown } from '@gravity-ui/icons';
import { Button, Popover, Tooltip } from '@heroui/react';
import { useMemo, useState } from 'react';

import { API_URL } from '../../api/client.ts';
import { PersonAvatar, personInitials } from '../ui/PersonAvatar.tsx';
import { CHARACTER_GROUPS, characterInfo, groupCharacters, listCharacters, searchCharacters } from './characterCatalog.ts';

const SHEETS = import.meta.glob('./characters/char_*.png', { eager: true, query: '?url', import: 'default' }) as Record<string, string>;
const sheetOf = (index: number) => SHEETS[`./characters/char_${index}.png`];

/** The character's idle frame (facing down, walk frame 1); `crop` shows the head and shoulders only. */
export function CharacterFace({ index, zoom = 2, crop, className = '' }: { index: number; zoom?: number; crop?: boolean; className?: string }) {
  const sheet = Number.isInteger(index) && index >= 0 && index < MEMBER_CHARACTER_COUNT ? sheetOf(index) : undefined;
  if (!sheet) return <span className={`inline-block shrink-0 ${className}`} style={{ width: 16 * zoom, height: (crop ? 18 : 32) * zoom }} aria-hidden="true" />;
  return (
    <span
      aria-hidden="true"
      className={`inline-block shrink-0 ${className}`}
      style={{
        width: 16 * zoom,
        height: (crop ? 18 : 32) * zoom,
        backgroundImage: `url(${sheet})`,
        backgroundSize: `${112 * zoom}px ${96 * zoom}px`,
        backgroundPosition: `${-16 * zoom}px 0`,
        backgroundRepeat: 'no-repeat',
        imageRendering: 'pixelated',
      }}
    />
  );
}

type FaceMember = Pick<OrgTeamMemberV2, 'name' | 'subjectId' | 'avatar' | 'useCharacter' | 'pictureUrl' | 'color'> & { human?: boolean | undefined };

/** What picture a member shows, in words (also the editor's mode): a person with no picture chosen shows the organisation's. */
export function pictureMode(m: FaceMember): 'character' | 'address' | 'organisation' {
  if (m.pictureUrl && !m.useCharacter) return 'address';
  if (m.human && m.subjectId && !m.useCharacter) return 'organisation';
  return 'character';
}

/** A small round face for a row or a header: square, 32 px. */
export function MemberFace({ member, index }: { member: FaceMember; index?: number }) {
  const mode = pictureMode(member);
  const tint = member.color && /^#[0-9a-f]{6}$/i.test(member.color) ? member.color : undefined;
  if (mode === 'address' && member.pictureUrl) {
    return <img src={member.pictureUrl} alt="" referrerPolicy="no-referrer" className="size-8 shrink-0 rounded-full object-cover" />;
  }
  if (mode === 'organisation' && member.subjectId) {
    return <PersonAvatar name={member.name || '?'} picture={`${API_URL}/people/v1/${encodeURIComponent(member.subjectId)}/picture`} />;
  }
  const avatar = member.avatar ?? index;
  if (avatar !== undefined) {
    return (
      <span className="inline-flex size-8 shrink-0 items-end justify-center overflow-hidden rounded-full bg-surface-secondary" style={tint ? { boxShadow: `inset 0 0 0 2px ${tint}` } : undefined}>
        <CharacterFace index={avatar} zoom={1.5} crop />
      </span>
    );
  }
  return (
    <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-surface-secondary text-xs font-medium" style={tint ? { boxShadow: `inset 0 0 0 2px ${tint}` } : undefined}>
      {personInitials(member.name || '?')}
    </span>
  );
}

/** Choose one of the workbench's characters by name: the one chosen, and the rest on demand under a search. */
export function CharacterPicker({ value, onChange, memberName }: { value: number | undefined; onChange: (index: number) => void; memberName?: string }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const all = useMemo(() => listCharacters(MEMBER_CHARACTER_COUNT), []);
  const shown = useMemo(() => groupCharacters(searchCharacters(all, query)), [all, query]);
  const current = value === undefined ? undefined : characterInfo(value);
  return (
    <Popover
      isOpen={open}
      onOpenChange={(next) => {
        if (next) setQuery('');
        setOpen(next);
      }}
    >
      <Button variant="secondary" className="h-auto w-fit justify-start gap-2.5 px-2 py-1.5" aria-label={`${memberName ? `Character for ${memberName}` : 'Character'}: ${current?.name ?? 'none chosen'}. Change`}>
        {value !== undefined ? <CharacterFace index={value} zoom={1.5} /> : null}
        <span className="text-sm">{current?.name ?? 'Choose a character'}</span>
        <ChevronDown className="size-3.5 shrink-0 text-muted" />
      </Button>
      <Popover.Content placement="bottom start" className="w-[23rem] max-w-[calc(100vw-1rem)]">
        <Popover.Dialog className="grid gap-2 p-3" aria-label="Choose a character">
          <input className="w-full rounded-lg border border-field-border bg-field-background px-3 py-1.5 text-sm outline-none focus:border-accent" type="search" aria-label="Search characters" placeholder="Search: chef, robot, uniform" value={query} onChange={(e) => setQuery(e.target.value)} />
          <div className="grid max-h-80 gap-3 overflow-y-auto pe-1">
            {shown.length === 0 && <span className="text-sm text-muted">No character matches.</span>}
            {shown.map((g) => (
              <section key={g.id} className="grid gap-1">
                <h3 className="m-0 text-xs font-medium text-muted">{CHARACTER_GROUPS.find((x) => x.id === g.id)?.label}</h3>
                <div className="flex flex-wrap gap-0.5">
                  {g.characters.map((c) => (
                    <Tooltip key={c.index} delay={250} closeDelay={0}>
                    <Button
                      variant="ghost"
                      aria-label={c.name}
                      aria-pressed={c.index === value}
                      onPress={() => {
                        onChange(c.index);
                        setOpen(false);
                      }}
                      className={`h-auto min-w-0 rounded-lg border p-1 ${c.index === value ? 'border-accent bg-accent/10' : 'border-transparent hover:border-separator'}`}
                    >
                      <CharacterFace index={c.index} zoom={2} />
                    </Button>
                    <Tooltip.Content>{c.name}</Tooltip.Content>
                    </Tooltip>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </Popover.Dialog>
      </Popover.Content>
    </Popover>
  );
}
